import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Grid,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import CloseIcon from "@mui/icons-material/Close";
import * as MuiIcons from "@mui/icons-material";
import api, { API_BASE_URL } from "../../../../utils/api";

const softInsetSx = {
  borderRadius: "14px",
  border: "1px solid rgba(47,99,173,0.08)",
  backgroundColor: "rgba(47,99,173,0.03)",
};

const primaryButtonSx = {
  backgroundColor: "#2f63ad",
  color: "white",
  fontWeight: 700,
  borderRadius: "10px",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
};

const secondaryButtonSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  borderColor: "#2f63ad",
  color: "#2f63ad",
  "&:hover": { backgroundColor: "rgba(47,99,173,0.04)" },
};

const dialogPaperSx = {
  borderRadius: "18px",
  background: "rgba(255,255,255,0.98)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
};

const RequestFilesViewer = ({ requestId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewModal, setPreviewModal] = useState({
    open: false,
    src: "",
    isPdf: false,
    blobUrl: null,
    loading: false,
  });

  useEffect(() => {
    if (!requestId) return;
    const fetchFiles = async () => {
      try {
        const res = await api.get(`/request-files/${requestId}`);
        setFiles(res.data || []);
      } catch (err) {
        console.error("Failed to fetch request files", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFiles();
  }, [requestId]);

  const openPreview = useCallback(async (src, isPdf) => {
    if (!isPdf) {
      setPreviewModal({ open: true, src, isPdf: false, blobUrl: null, loading: false });
      return;
    }
    setPreviewModal({ open: true, src, isPdf: true, blobUrl: null, loading: true });
    try {
      const res = await fetch(src, { credentials: "include" });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreviewModal((prev) => ({ ...prev, blobUrl, loading: false }));
    } catch (err) {
      console.error("PDF blob fetch failed, falling back to direct URL", err);
      setPreviewModal((prev) => ({ ...prev, blobUrl: null, loading: false }));
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewModal((prev) => {
      if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
      return { open: false, src: "", isPdf: false, blobUrl: null, loading: false };
    });
  }, []);

  if (loading) return <CircularProgress sx={{ mt: 2 }} />;
  if (!files.length)
    return (
      <Typography variant="body2" sx={{ color: "#6b778c", mt: 1 }}>
        No files uploaded for this request.
      </Typography>
    );

  return (
    <>
      <Grid container spacing={2}>
        {files.map((file) => {
          const fileUrl = `${API_BASE_URL}/request-images/${encodeURIComponent((file.file_url || "").split("/").pop())}`;
          const ext = (file.file_name || "").split(".").pop()?.toLowerCase() || "";
          const fileType = file.file_type || "";
          const isImage =
            fileType.startsWith("image/") ||
            ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);
          const isPDF = fileType.includes("pdf") || ext === "pdf";

          return (
            <Grid item xs={12} sm={6} md={4} key={file.id}>
              <Card
                sx={{
                  ...softInsetSx,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  textAlign: "center",
                  height: "100%",
                  backgroundColor: "#fff",
                }}
              >
                {isImage ? (
                  <>
                    <Box
                      component="img"
                      src={fileUrl}
                      alt={file.file_name}
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 120,
                        borderRadius: "8px",
                        cursor: "pointer",
                        objectFit: "cover",
                      }}
                      onClick={() => openPreview(fileUrl, false)}
                    />
                    <Typography variant="body2" sx={{ mt: 0.5, wordBreak: "break-word" }}>
                      {file.file_name}
                    </Typography>
                    <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => openPreview(fileUrl, false)}
                        sx={secondaryButtonSx}
                      >
                        Preview
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        sx={primaryButtonSx}
                      >
                        Download
                      </Button>
                    </Box>
                  </>
                ) : isPDF ? (
                  <>
                    <Box
                      sx={{
                        minHeight: 120,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      <MuiIcons.PictureAsPdf color="error" sx={{ fontSize: 52 }} />
                    </Box>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                      {file.file_name}
                    </Typography>
                    <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => openPreview(fileUrl, true)}
                        sx={secondaryButtonSx}
                      >
                        Preview
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        sx={primaryButtonSx}
                      >
                        Download
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        minHeight: 120,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      <FolderOutlinedIcon sx={{ fontSize: 52, color: "#2f63ad" }} />
                    </Box>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                      {file.file_name}
                    </Typography>
                    <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={secondaryButtonSx}
                      >
                        Open
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        sx={primaryButtonSx}
                      >
                        Download
                      </Button>
                    </Box>
                  </>
                )}
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Preview dialog */}
      <Dialog
        open={previewModal.open}
        onClose={closePreview}
        maxWidth={previewModal.isPdf ? "xl" : "lg"}
        fullWidth
        PaperProps={{
          sx: {
            ...dialogPaperSx,
            ...(previewModal.isPdf && { height: "92vh" }),
          },
        }}
      >
        <DialogContent
          sx={{
            position: "relative",
            backgroundColor: previewModal.isPdf ? "#fff" : "#111",
            p: previewModal.isPdf ? 0 : 2,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <IconButton
            onClick={closePreview}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 10,
              bgcolor: previewModal.isPdf ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.45)",
              "&:hover": {
                bgcolor: previewModal.isPdf ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.7)",
              },
            }}
          >
            <CloseIcon sx={{ color: previewModal.isPdf ? "#223142" : "white" }} />
          </IconButton>

          {previewModal.isPdf ? (
            previewModal.loading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  minHeight: "80vh",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <iframe
                src={previewModal.blobUrl || previewModal.src}
                title="PDF Preview"
                width="100%"
                style={{ flex: 1, border: "none", borderRadius: "18px", minHeight: "80vh" }}
              />
            )
          ) : (
            <Box
              component="img"
              src={previewModal.src}
              alt="preview"
              sx={{
                maxWidth: "100%",
                maxHeight: "85vh",
                objectFit: "contain",
                borderRadius: "10px",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RequestFilesViewer;
